import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  FaSearch, FaFilter, FaPlus, FaEdit, FaTrash, FaReceipt, FaInbox,
  FaSortAmountDown, FaSortAmountUp, FaDollarSign, FaCalendarAlt, FaStore
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import Pagination from '../../components/shared/Pagination';
import { fetchBusinessExpenses, deleteBusinessExpense } from './financialService';
import { formatCurrency } from '../../utils/utils';
import { ResponsiveHeader, StatusBadge } from '../../components/styled/CommonStyles';
import {
  GridContainer, Card, CardHeader, CardContent, InfoRow,
  ActionIconsContainer, ActionButton, EmptyState, FilterSection, FilterGroup, FilterLabel, FilterButton
} from '../../components/styled/GridComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import Modal from '../../components/shared/Modal';
import BusinessExpenseForm from './BusinessExpenseForm';

const TotalBanner = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  margin-bottom: ${props => props.theme.spacing(3)};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalCount = styled.span`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
`;

const TotalAmount = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: #dc3545;
`;

const categoryColors = {
  'Advertisement': '#e91e63', 'Insurance': '#9c27b0', 'Marketing': '#673ab7',
  'Licensing & Fees': '#3f51b5', 'Office Supplies': '#2196f3', 'Vehicle': '#00bcd4',
  'Vehicle Fuel': '#0288d1', 'Equipment Fuel': '#0097a7', 'Maintenance': '#8bc34a',
  'COGS': '#795548', 'General Labor / Helper': '#ff5722',
  'Software & Subscriptions': '#009688', 'Rent & Utilities': '#4caf50',
  'Professional Services': '#ff9800', 'Other': '#607d8b'
};

const CATEGORIES = [
  'Advertisement', 'Insurance', 'Marketing', 'Licensing & Fees', 'Office Supplies',
  'Vehicle', 'Vehicle Fuel', 'Equipment Fuel', 'Maintenance', 'COGS', 'General Labor / Helper',
  'Software & Subscriptions', 'Rent & Utilities', 'Professional Services', 'Other'
];

const formatExpenseDate = (value) => {
  if (!value) return '';
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
};

const BusinessExpenseList = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState('desc');
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const itemsPerPage = 20;

  useEffect(() => { loadExpenses(); }, []);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const data = await fetchBusinessExpenses({ period: '12months' });
      setExpenses(data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedExpenseId) return;
    try {
      await deleteBusinessExpense(selectedExpenseId);
      setFeedbackSuccess(true);
      setFeedbackMessage('Expense deleted');
      setShowFeedback(true);
      setDeleteModalOpen(false);
      setSelectedExpenseId(null);
      loadExpenses();
    } catch (error) {
      setFeedbackSuccess(false);
      setFeedbackMessage('Failed to delete expense');
      setShowFeedback(true);
      setDeleteModalOpen(false);
      setSelectedExpenseId(null);
    }
  };

  const handleFeedbackComplete = () => {
    setShowFeedback(false);
  };

  const filteredExpenses = useMemo(() => {
    let result = [...expenses];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.description?.toLowerCase().includes(q) ||
        e.vendor?.toLowerCase().includes(q) ||
        e.category?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter) result = result.filter(e => e.category === categoryFilter);
    result.sort((a, b) => {
      const dateA = new Date(a.expenseDate);
      const dateB = new Date(b.expenseDate);
      return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    });
    return result;
  }, [expenses, search, categoryFilter, sortDirection]);

  const totalAmount = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);
  const paginatedExpenses = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredExpenses.slice(start, start + itemsPerPage);
  }, [filteredExpenses, currentPage]);
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);

  if (loading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Business Expenses"
        onSearch={(val) => { setSearch(val); setCurrentPage(1); }}
        onAdd={() => { setEditingExpense(null); setShowForm(true); }}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel><FaFilter /> Category</FilterLabel>
          <Select
            label="Category"
            icon={<FaFilter />}
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
        </FilterGroup>
        <FilterGroup>
          <FilterButton onClick={() => setSortDirection(d => d === 'desc' ? 'asc' : 'desc')}>
            {sortDirection === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />} Date
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <TotalBanner>
        <TotalCount>{filteredExpenses.length} expense{filteredExpenses.length !== 1 ? 's' : ''}</TotalCount>
        <TotalAmount>{formatCurrency(totalAmount)}</TotalAmount>
      </TotalBanner>

      <GridContainer>
        {paginatedExpenses.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No expenses found. Add your first expense to start tracking.</p>
          </EmptyState>
        ) : (
          paginatedExpenses.map(expense => (
            <Card key={expense.id}>
              <CardHeader>
                <h3>{expense.description}</h3>
                <StatusBadge color={categoryColors[expense.category] || '#607d8b'}>
                  {expense.category}
                </StatusBadge>
              </CardHeader>
              <CardContent>
                <InfoRow>
                  <FaDollarSign />
                  <strong style={{ color: '#dc3545' }}>{formatCurrency(expense.amount)}</strong>
                </InfoRow>
                {expense.vendor && (
                  <InfoRow>
                    <FaStore />
                    <span>{expense.vendor}</span>
                  </InfoRow>
                )}
                <InfoRow>
                  <FaCalendarAlt />
                  <span>{formatExpenseDate(expense.expenseDate)}</span>
                </InfoRow>
                {expense.referenceNumber && (
                  <InfoRow>
                    <FaReceipt />
                    <span>Ref: {expense.referenceNumber}</span>
                  </InfoRow>
                )}
              </CardContent>
              <ActionIconsContainer>
                <ActionButton
                  onClick={() => { setEditingExpense(expense); setShowForm(true); }}
                  title="Edit Expense"
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton
                  onClick={() => { setSelectedExpenseId(expense.id); setDeleteModalOpen(true); }}
                  title="Delete Expense"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </Card>
          ))
        )}
      </GridContainer>

      {filteredExpenses.length > itemsPerPage && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      )}

      {showForm && (
        <BusinessExpenseForm
          expense={editingExpense}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            setFeedbackSuccess(true);
            setFeedbackMessage(editingExpense ? 'Expense updated' : 'Expense added');
            setShowFeedback(true);
            loadExpenses();
          }}
        />
      )}

      <Modal
        isOpen={deleteModalOpen}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
        onConfirm={handleDelete}
        onCancel={() => { setDeleteModalOpen(false); setSelectedExpenseId(null); }}
      />

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={handleFeedbackComplete}
      />
    </DashboardLayout>
  );
};

export default BusinessExpenseList;
